import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

/**
 * BackButton - WhatsApp/Instagram-style icon-only "<" chevron
 *
 * Two variants:
 *   - default : light card style. Sits on white / grey surfaces (top bar,
 *               sidebar, above-hero rows).
 *   - hero    : frosted-glass style tuned for dark hero blocks (Bills,
 *               Invoices, Other Payments, Other Receipts, etc.). Uses a
 *               translucent white pill with a subtle backdrop blur so the
 *               button reads as an affordance without a jarring white
 *               circle on a dark background.
 *
 * Set `desktopOnly` to true for page-level back buttons. On mobile / PWA
 * the top-bar back button in DashboardLayout already covers back navigation,
 * so an in-page back button there would be redundant clutter. Wrapping via
 * a Tailwind `hidden lg:...` breakpoint keeps the DOM identical on desktop
 * and hides it on all sub-lg viewports (mobile web AND installed PWA on
 * phones).
 */
export const BackButton = ({
  fallbackPath = '/',
  variant = 'default',
  desktopOnly = false,
  className = '',
  ...props
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  const base =
    (desktopOnly
      ? 'hidden lg:inline-flex '
      : 'inline-flex ') +
    'group items-center justify-center ' +
    'h-11 w-11 sm:h-10 sm:w-10 ' +
    'rounded-full ' +
    'transition-all duration-200 ease-out ' +
    'active:scale-90 ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ';

  const variants = {
    default:
      'text-slate-900 dark:text-white ' +
      'bg-white hover:bg-slate-50 dark:bg-slate-800 dark:hover:bg-slate-700 ' +
      'border border-slate-200 dark:border-slate-700 ' +
      'shadow-sm hover:shadow-md ' +
      'active:bg-slate-200 dark:active:bg-slate-600 ' +
      'focus-visible:ring-blue-500/60 ',
    hero:
      'text-white ' +
      'bg-white/10 hover:bg-white/20 ' +
      'backdrop-blur-sm ' +
      'border border-white/25 hover:border-white/40 ' +
      'shadow-[0_2px_8px_rgba(0,0,0,0.12)] ' +
      'active:bg-white/30 ' +
      'focus-visible:ring-white/70 ',
  };

  return (
    <button
      type="button"
      onClick={handleBack}
      aria-label="Go back"
      title="Go back"
      data-testid="back-button"
      className={base + (variants[variant] || variants.default) + className}
      {...props}
    >
      <ChevronLeft
        className="w-7 h-7 sm:w-6 sm:h-6 -ml-0.5 transition-transform duration-200 group-active:-translate-x-0.5"
        strokeWidth={3}
      />
    </button>
  );
};

export default BackButton;
